import type { InputSignal, TemplateRef, WritableSignal } from '@angular/core';
import { InjectionToken } from '@angular/core';

import { deepEqual } from 'fast-equals';
import type { BehaviorSubject } from 'rxjs';
import { Subject } from 'rxjs';

import type { BadgeColor } from '../../common/badge/badge.types';
import type { IconName } from '../../common/icon/icons.type';

export interface Select2Item<Value = any> {
  label: string;
  value: Value;
  disabled?: boolean;
  disabledTooltip?: string;
  icon?: IconName;
  badge?: {
    icon: IconName;
    color: BadgeColor;
    label: string;
    tooltip: string;
  };
}

export type SelectFilterFn = (item: Select2Item) => boolean;

export interface HeaderTemplate {
  id: string;
  index: number;
  template: TemplateRef<void>;
}

export type DefaultSelectContext = { items: Select2Item[] };
export type DefaultSelectSignalContext = { items: InputSignal<Select2Item[]> };
export type SelectContext = DefaultSelectSignalContext | DefaultSelectContext | null;

export type SelectItemType<T extends SelectContext | DefaultSelectSignalContext> = T extends {
  items: any[];
}
  ? T['items'] extends InputSignal<infer R>
    ? R extends Array<infer R2>
      ? R2
      : never
    : T['items'][number]
  : never;

export interface SelectResolvedTemplate<T = any> {
  templateRef: TemplateRef<T>;
}

export interface ItemContext<Item> {
  $implicit: ISelectController;
  item: Item;
  isHoverState: boolean;
  index: number;
  isLast: boolean;
}

export abstract class ItemSelector<Item extends Select2Item> {
  public selectionChanged$ = new Subject<void>();
  protected compareItemsFn = (a: Item, b: Item | { value: Item['value'] }) =>
    deepEqual(a.value, b.value);

  public abstract selected: WritableSignal<Item | Item[] | null>;
  public abstract setHiddenItems(items: Item | Item[] | null): void;
  public abstract select(item: Item | Item[]): void;
  public abstract clear(): void;
  public abstract isSelected(item: Item): boolean;
  public abstract getValue(): Item['value'] | null;
  public abstract setItems(items: Item[]): void;
  public abstract writeValue(value: Item['value'] | Array<Item['value']>): void;
  public abstract setCompareItemsFn(
    fn: (a: Item, b: Item | { value: Item['value'] }) => boolean,
  ): void;
}

export interface ISelectController<
  Select extends SelectContext | DefaultSelectSignalContext = {
    items: Select2Item[];
  },
  IS extends ItemSelector<SelectItemType<Select>> = ItemSelector<any>,
> {
  itemSelector: IS;
  isDisabled: WritableSignal<boolean>;
  isOpen: WritableSignal<boolean>;
  clear$: Subject<void>;
  select$: Subject<Select2Item | Select2Item[]>;
  keyDown$: Subject<KeyboardEvent>;
  navigation$: BehaviorSubject<{ type: 'item'; index: number; visible: boolean }>;
  focusTrigger$: Subject<void>;
  detectChanges$: Subject<void>;

  setDisabled: (value: boolean) => void;
  toggle: (value?: boolean) => void;
  scrollToIndex: (index: number) => void;
  isHovered: (itemIndex: number) => boolean;
}

export const SELECT_CONTROLLER = new InjectionToken<ISelectController>('Select controller');
